/**
 * API Client using XMLHttpRequest to bypass fetch interceptors (PostHog, etc.)
 * This avoids "Body is disturbed or locked" and "stream already read" errors
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

class ApiClient {
  /**
   * Make an HTTP request using XMLHttpRequest
   * @param {string} url - The URL to request
   * @param {Object} options - Request options
   * @returns {Promise<{ok: boolean, status: number, data: any}>}
   */
  static request(url, options = {}) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const method = options.method || 'GET';
      const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
      
      xhr.open(method, fullUrl, true);
      xhr.timeout = options.timeout || 30000; // 30 second default timeout
      
      // Set default Content-Type for JSON
      if (options.body && typeof options.body === 'string') {
        xhr.setRequestHeader('Content-Type', 'application/json');
      }
      
      // Set custom headers
      if (options.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }
      
      // Include credentials (cookies)
      xhr.withCredentials = true;
      
      xhr.onload = function() {
        let data = null;
        try {
          data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch (e) {
          // Response is not JSON
          data = { message: xhr.responseText };
        }
        
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          data,
          text: xhr.responseText,
        });
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error. Please check your internet connection.'));
      };
      
      xhr.ontimeout = function() {
        reject(new Error('Request timed out. Please try again.'));
      };
      
      // Send request
      if (options.body) {
        xhr.send(options.body);
      } else {
        xhr.send();
      }
    });
  }

  /**
   * POST request
   */
  static post(url, body, headers = {}) {
    return this.request(url, {
      method: 'POST',
      headers,
      body: typeof body === 'string' ? body : JSON.stringify(body),
    });
  }

  /**
   * PUT request
   */
  static put(url, body, headers = {}) {
    return this.request(url, {
      method: 'PUT',
      headers,
      body: typeof body === 'string' ? body : JSON.stringify(body),
    });
  }

  /**
   * GET request
   */
  static get(url, headers = {}) {
    return this.request(url, {
      method: 'GET',
      headers,
    });
  }

  /**
   * DELETE request
   */
  static delete(url, headers = {}) {
    return this.request(url, {
      method: 'DELETE',
      headers,
    });
  }

  /**
   * Parse error response and return user-friendly message
   */
  static parseError(errorData, defaultMessage = 'An error occurred') {
    if (!errorData) return defaultMessage;
    
    if (errorData.detail) {
      if (typeof errorData.detail === 'string') {
        return errorData.detail;
      }
      // Pydantic validation errors
      if (Array.isArray(errorData.detail)) {
        return errorData.detail.map(err => {
          const field = err.loc ? err.loc.join('.').replace('body.', '') : 'Field';
          return `${field}: ${err.msg}`;
        });
      }
    }
    
    if (errorData.message) {
      return errorData.message;
    }
    
    return defaultMessage;
  }
}

export default ApiClient;
